/// Single chat message component for the AI chat panel.
///
/// User messages are right-aligned with blue background, assistant messages
/// are left-aligned with zinc background. Assistant messages render streaming
/// markdown via Streamdown. Error messages show a red border with retry option.

import { Streamdown } from "streamdown";
import "streamdown/styles.css";
import { code } from "@streamdown/code";
import type { ChatMessage } from "@/types/ai";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AIChatMessageProps {
  message: ChatMessage;
  isStreaming: boolean;
  onRetry?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format timestamp to HH:MM. */
function formatTime(ts: number): string {
  const date = new Date(ts);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AIChatMessage({ message, isStreaming, onRetry }: AIChatMessageProps) {
  const isUser = message.role === "user";
  const isError = message.isError === true;

  if (isUser) {
    return (
      <div className="flex justify-end mb-3">
        <div className="max-w-[80%]">
          <div className="rounded-lg bg-blue-600/20 border border-blue-500/20 px-3 py-2">
            <p className="text-sm text-zinc-200 whitespace-pre-wrap">{message.content}</p>
          </div>
          <p className="mt-0.5 text-right text-[10px] text-zinc-600">
            {formatTime(message.timestamp)}
          </p>
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div className="flex justify-start mb-3">
      <div className="max-w-[90%]">
        <div
          className={[
            "rounded-lg px-3 py-2",
            isError
              ? "bg-red-900/20 border border-red-500/30"
              : "bg-zinc-800 border border-zinc-700/50",
          ].join(" ")}
        >
          {message.content ? (
            <div className="text-sm text-zinc-200 streamdown-dark">
              <Streamdown
                mode={isStreaming ? "streaming" : "static"}
                isAnimating={isStreaming}
                plugins={{ code }}
                shikiTheme={["github-dark", "github-dark"]}
              >
                {message.content}
              </Streamdown>
            </div>
          ) : isStreaming ? (
            <div className="flex items-center gap-1.5 py-1">
              <span className="h-1.5 w-1.5 rounded-full bg-zinc-500 animate-pulse" />
              <span className="h-1.5 w-1.5 rounded-full bg-zinc-500 animate-pulse [animation-delay:150ms]" />
              <span className="h-1.5 w-1.5 rounded-full bg-zinc-500 animate-pulse [animation-delay:300ms]" />
            </div>
          ) : null}
        </div>
        <div className="mt-0.5 flex items-center gap-2">
          {message.model && <span className="text-[10px] text-zinc-600">{message.model}</span>}
          <span className="text-[10px] text-zinc-600">{formatTime(message.timestamp)}</span>
          {isError && onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="text-[10px] text-red-400 hover:text-red-300 transition-colors"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
